import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, Plus, Save, Trash2, Youtube } from 'lucide-react';
import { fetchSystemConfig, saveSystemConfig } from '../../../services/firebaseService';

interface YouTubeChannel {
  channelId: string;
  name: string;
}

const AdminCreatorsPanel: React.FC = () => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState<YouTubeChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New channel input
  const [newChannelId, setNewChannelId] = useState('');
  const [newChannelName, setNewChannelName] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const config = await fetchSystemConfig('youtube_creators');
        setChannels(config?.channels || []);
      } catch (e: any) {
        setError(e.message);
      }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveSystemConfig('youtube_creators', { channels, updatedAt: new Date() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e: any) {
      setError(e.message);
    }
    setSaving(false);
  };

  const handleAdd = () => {
    const id = newChannelId.trim();
    const name = newChannelName.trim();
    if (!id || !name) return;
    if (channels.some(c => c.channelId === id)) {
      setError(t('admin.creators_duplicate', 'Denne kanalen er allerede lagt til'));
      return;
    }
    setChannels([...channels, { channelId: id, name }]);
    setNewChannelId('');
    setNewChannelName('');
    setError(null);
  };

  const handleRemove = (idx: number) => {
    setChannels(channels.filter((_, i) => i !== idx));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[var(--text-muted)]">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-in slide-in-from-bottom-4 max-h-[85vh] overflow-y-auto pr-2 custom-scrollbar">
      <div className="bg-red-500/5 border border-red-500/20 p-4 md:p-6 rounded-panel space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-red-400">
            <Youtube size={20} />
            <div>
              <h3 className="text-tiny font-black uppercase tracking-widest">{t('admin.creators_title', 'Innholdsskapere')}</h3>
              <p className="text-3xs font-bold opacity-70">{t('admin.creators_subtitle', 'YouTube-kanaler som AI-journalisten prioriterer')}</p>
            </div>
          </div>
          {saved && (
            <span className="flex items-center gap-1 text-emerald-400 text-3xs font-black uppercase tracking-widest animate-in fade-in">
              <CheckCircle size={12} /> {t('common.saved', 'Lagret!')}
            </span>
          )}
        </div>

        <p className="text-3xs text-[var(--text-muted)] leading-relaxed">
          {t('admin.creators_help', 'Legg til YouTube Channel IDs for kanaler du vil at AI-journalisten skal prioritere. Artikkelgeneratoren søker først i disse kanalene etter fersk, relevant video.')}
        </p>

        {error && (
          <div className="text-2xs font-bold px-4 py-2 rounded-xl bg-red-500/10 text-red-400">{error}</div>
        )}

        {/* Channel list */}
        {channels.length > 0 && (
          <div className="space-y-2">
            {channels.map((ch, idx) => (
              <div key={ch.channelId} className="flex items-center gap-3 bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-xl p-3">
                <Youtube size={16} className="text-red-400/60 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-tiny font-bold text-[var(--text-primary)] truncate">{ch.name}</p>
                  <p className="text-3xs text-[var(--text-muted)] font-mono truncate">{ch.channelId}</p>
                </div>
                <button
                  onClick={() => handleRemove(idx)}
                  className="p-1.5 rounded-lg hover:bg-red-500/10 text-[var(--text-muted)] hover:text-red-400 transition-colors flex-shrink-0"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {channels.length === 0 && (
          <div className="text-center py-6">
            <Youtube size={28} className="mx-auto text-[var(--text-muted)] mb-2" />
            <p className="text-2xs text-[var(--text-muted)]">{t('admin.creators_empty', 'Ingen kanaler lagt til ennå')}</p>
          </div>
        )}

        {/* Add new channel */}
        <div className="bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-xl p-3 space-y-2">
          <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('admin.creators_add', 'Legg til kanal')}</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={newChannelName}
              onChange={(e) => setNewChannelName(e.target.value)}
              placeholder={t('admin.creators_name_placeholder', 'Kanalnavn (f.eks. Bobiansen)')}
              className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2.5 text-tiny text-[var(--text-primary)] focus:outline-none focus:border-red-500 placeholder:text-[var(--text-muted)]"
            />
            <input
              type="text"
              value={newChannelId}
              onChange={(e) => setNewChannelId(e.target.value)}
              placeholder={t('admin.creators_id_placeholder', 'Channel ID (UC...)')}
              className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2.5 text-tiny text-[var(--text-secondary)] font-mono focus:outline-none focus:border-red-500 placeholder:text-[var(--text-muted)]"
            />
            <button
              onClick={handleAdd}
              disabled={!newChannelId.trim() || !newChannelName.trim()}
              className="px-3 py-2.5 rounded-xl bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/30 transition-all disabled:opacity-30"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        {/* Save button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className={`w-full py-4 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 ${saved ? 'bg-emerald-600 text-white' : 'bg-red-600/80 hover:bg-red-600 text-white'}`}
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : saved ? <CheckCircle size={16} /> : <Save size={16} />}
          {saved ? t('common.saved', 'Lagret!') : t('admin.save_changes', 'Lagre endringer')}
        </button>
      </div>
    </div>
  );
};

export default AdminCreatorsPanel;
